/**
 * RhythmicDictationInput — controlled grid for rhythmic-dictation exercises.
 *
 * Renders a row of toggleable cells, one per beat subdivision. The user taps
 * cells to mark note attacks; empty cells are rests or note continuations.
 *
 * Layout: cells split into two rows to keep touch targets large:
 *   4/4 (8 cells): row 1 = beats 1–2 (cells 0–3), row 2 = beats 3–4 (cells 4–7)
 *   6/8 (6 cells): row 1 = beat 1 (cells 0–2),    row 2 = beat 2 (cells 3–5)
 *
 * Controlled — parent owns `grid` (boolean[]).
 */

import { type ReactNode } from "react";

export type RhythmicCellStatus = "correct" | "wrong";

export interface RhythmicDictationInputProps {
  meter: "4/4" | "6/8";
  /** Current grid state (controlled). Length must match meter: 8 for 4/4, 6 for 6/8. */
  grid: boolean[];
  onChange: (grid: boolean[]) => void;
  /** After grading, pass statuses to colour cells. Length = grid.length. */
  cellStatuses?: ReadonlyArray<RhythmicCellStatus | null>;
  /** Disables all cell toggles (during playback or after submit). */
  disabled?: boolean;
}

export function RhythmicDictationInput({
  meter,
  grid,
  onChange,
  cellStatuses,
  disabled = false,
}: RhythmicDictationInputProps): ReactNode {
  const cellsPerRow = meter === "4/4" ? 4 : 3;
  const totalCells = meter === "4/4" ? 8 : 6;
  const cpb = meter === "4/4" ? 2 : 3; // cells per beat

  const toggle = (i: number) => {
    if (disabled) return;
    const next = [...grid];
    next[i] = !(next[i] ?? false);
    onChange(next);
  };

  const clear = () => {
    if (disabled) return;
    onChange(Array<boolean>(totalCells).fill(false));
  };

  // Beat label beneath each cell
  const cellLabel = (i: number): string => {
    if (meter === "4/4") {
      const beat = Math.floor(i / cpb) + 1;
      return i % cpb === 0 ? String(beat) : "+";
    }
    // 6/8: label as "1", "2", "3" per compound beat
    const beat = Math.floor(i / cpb) + 1;
    const pos  = (i % cpb) + 1;
    return beat === 1 && pos === 1 ? "1" : pos === 1 ? "2" : String(pos);
  };

  const rows: number[][] = [];
  for (let r = 0; r * cellsPerRow < totalCells; r++) {
    rows.push(
      Array.from({ length: cellsPerRow }, (_, k) => r * cellsPerRow + k),
    );
  }

  const filledCount = grid.filter(Boolean).length;

  return (
    <div className="space-y-4" data-testid="rhythmic-dictation-input">
      {rows.map((rowIdx, rIdx) => (
        <div key={rIdx} className="space-y-1">
          <div className={`grid gap-2 ${cellsPerRow === 4 ? "grid-cols-4" : "grid-cols-3"}`}>
            {rowIdx.map((cellIdx) => {
              const isOn = grid[cellIdx] ?? false;
              const status = cellStatuses?.[cellIdx] ?? null;
              return (
                <button
                  key={cellIdx}
                  type="button"
                  onClick={() => toggle(cellIdx)}
                  disabled={disabled && status === null}
                  aria-pressed={isOn}
                  aria-label={`${cellLabel(cellIdx)} — ${isOn ? "note" : "rest"}`}
                  className={cellClass(isOn, status, disabled)}
                >
                  {isOn && (
                    <span
                      className="block w-4 h-4 rounded-full mx-auto"
                      style={{ backgroundColor: status ? undefined : "currentColor" }}
                      aria-hidden
                    />
                  )}
                </button>
              );
            })}
          </div>
          <div className={`grid gap-2 ${cellsPerRow === 4 ? "grid-cols-4" : "grid-cols-3"}`}>
            {rowIdx.map((cellIdx) => (
              <div
                key={cellIdx}
                className="text-center font-mono text-[10px] text-inkFaded select-none"
                aria-hidden
              >
                {cellLabel(cellIdx)}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between mt-1">
        <button
          type="button"
          onClick={clear}
          disabled={disabled || filledCount === 0}
          className="py-1 px-2 text-xs border border-ink/20 hover:bg-ink hover:text-paper transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Clear
        </button>
        <span className="font-mono text-[10px] text-inkFaded" aria-live="polite">
          {filledCount === 0 ? "no cells filled" : `${filledCount} cell${filledCount === 1 ? "" : "s"} filled`}
        </span>
      </div>
    </div>
  );
}

function cellClass(
  isOn: boolean,
  status: RhythmicCellStatus | null | undefined,
  disabled: boolean,
): string {
  const base =
    "py-4 px-2 border flex items-center justify-center min-h-[48px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2";
  const interactable = !disabled || status !== null;
  const cursor = interactable ? "" : "cursor-default";

  if (status === "correct" && isOn) {
    // Correctly filled cell
    return `${base} bg-accent border-accent text-paper ${cursor}`;
  }
  if (status === "correct" && !isOn) {
    // Correctly empty cell — faint green outline
    return `${base} bg-paper border-accent/40 ${cursor}`;
  }
  if (status === "wrong") {
    // Wrong — either filled when should be empty, or empty when should be filled
    return `${base} ${isOn ? "bg-ink text-paper border-ink" : "bg-ink/5 border-ink/60 border-dashed"} ${cursor}`;
  }

  // No status yet
  if (isOn) {
    return `${base} bg-ink text-paper border-ink ${disabled ? cursor : "hover:bg-ink/80 active:scale-95"}`;
  }
  return `${base} bg-paper border-ink/20 text-ink ${disabled ? cursor : "hover:bg-paperWarm active:scale-95"}`;
}
